import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

interface AmbulanceProvider {
  id: string;
  provider_name: string;
  provider_type: string;
  email: string;
  contact_number: string;
  alt_contact: string;
  city: string;
  state: string;
  base_location: string;
  pincode: string;
  registration_number: string;
  license_url: string;
  photo_url: string;
  /** 'logo' means render with object-fit:contain on white everywhere (dashboard
   * preview + public card/detail page) instead of a hard object-fit:cover crop. */
  image_type?: string | null;
  latitude: number;
  longitude: number;
  city_id?: string;
  state_id?: string;
  area_id?: string;
  area?: string;
  kyc_status: string;
  rejection_reason?: string;
  is_verified: boolean;
  is_active: boolean;
  subscription_plan: string;
  terms_accepted: boolean;
  adminSession?: boolean;
  email_notifications?: boolean;
  sms_notifications?: boolean;
  booking_alerts?: boolean;
  booking_enabled?: boolean;
  hours_enabled?: boolean;
}

interface AmbulanceAuthContextType {
  provider: AmbulanceProvider | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminSession: boolean;
  providerToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string; reason?: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshProvider: () => Promise<void>;
}

const AmbulanceAuthContext = createContext<AmbulanceAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'ambulance_auth';
const TOKEN_KEY = 'ambulance_auth_token';

export const AmbulanceAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [provider, setProvider] = useState<AmbulanceProvider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  // Initialize from localStorage — always verify server-side, no localStorage flag trust
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        verifyProvider(parsed.id).then((result) => {
          if (result.shouldAccept) {
            // verifyProvider() re-fetches kyc_status fresh from the DB — merge
            // it in so an admin approval/rejection made while this tab sat
            // open (or since the last login) shows up on reload instead of
            // the stale cached copy sticking around until the next full login.
            const refreshed = result.kycStatus !== undefined
              ? { ...parsed, kyc_status: result.kycStatus }
              : parsed;
            setProvider(refreshed);
            if (result.kycStatus !== undefined) {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(refreshed));
            }
            // Only honour adminSession flag that was set by the server login flow
            setIsAdminSession(parsed.adminSession === true);
            const storedToken = localStorage.getItem(TOKEN_KEY);
            if (storedToken) setProviderToken(storedToken);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_KEY);
          }
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Graceful verification - returns shouldAccept: true if API fails or doesn't exist
  const verifyProvider = async (id: string): Promise<{ shouldAccept: boolean; isActive?: boolean; kycStatus?: string }> => {
    try {
      const result = await bridgeApi.call<any>('ambulance_provider_verify', { id });
      // API responded successfully
      if (result?.is_active === true) {
        return { shouldAccept: true, isActive: true, kycStatus: result?.kyc_status };
      }
      if (result?.is_active === false) {
        return { shouldAccept: false, isActive: false };
      }
      // Unknown response - accept session
      return { shouldAccept: true, kycStatus: result?.kyc_status };
    } catch (error: any) {
      // API failed or doesn't exist - gracefully accept session
      console.warn('[AmbulanceAuth] Verification skipped (API unavailable):', error.message);
      return { shouldAccept: true };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string; reason?: string }> => {
    setIsLoading(true);

    try {
      const result = await bridgeApi.post<any>('ambulance_login', { email, password });

      if (!result.success) {
        setIsLoading(false);
        return { success: false, message: result.message || 'Login failed', reason: result.deactivation_reason || undefined };
      }

      const prov = result.provider;

      // Allow login for all KYC statuses so providers can access dashboard to submit/resubmit KYC
      // Only block truly inactive accounts
      if (prov.is_active === false || prov.is_active === 0 || prov.is_active === '0') {
        setIsLoading(false);
        return { success: false, message: 'Your account is currently inactive. Please contact support.' };
      }

      const providerData: AmbulanceProvider = {
        id: prov.id,
        provider_name:
          prov.provider_name ||
          prov.business_name ||
          prov.full_name ||
          prov.contact_person ||
          '',

        provider_type: prov.provider_type || 'individual',
        email: prov.email,
        contact_number: prov.contact_number,
        alt_contact: prov.alt_contact || '',
        city: prov.city,
        state: prov.state,
        base_location: prov.base_location || '',
        pincode: prov.pincode || '',
        registration_number: prov.registration_number || '',
        license_url: prov.license_url || '',
        photo_url: prov.photo_url || '',
        image_type: prov.image_type || null,
        latitude: prov.latitude || 0,
        longitude: prov.longitude || 0,
        kyc_status: prov.kyc_status,
        rejection_reason: prov.rejection_reason || '',
        is_verified: prov.is_verified || false,
        is_active: prov.is_active,
        subscription_plan: prov.subscription_plan || 'free',
        terms_accepted: prov.terms_accepted || false,
        booking_enabled: prov.booking_enabled === true || prov.booking_enabled === '1' || prov.booking_enabled === 1,
        hours_enabled: prov.hours_enabled === true || prov.hours_enabled === '1' || prov.hours_enabled === 1,
        email_notifications: prov.email_notifications === true || prov.email_notifications === '1' || prov.email_notifications === 1,
        sms_notifications: prov.sms_notifications === true || prov.sms_notifications === '1' || prov.sms_notifications === 1,
        booking_alerts: prov.booking_alerts === true || prov.booking_alerts === '1' || prov.booking_alerts === 1,
      };
      setProvider(providerData);
      setIsAdminSession(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(providerData));

      if (result.token) {
        setProviderToken(result.token);
        localStorage.setItem(TOKEN_KEY, result.token);
      }

      setIsLoading(false);

      return { success: true, message: 'Login successful!' };
    } catch (err: any) {
      setIsLoading(false);
      // bridgeApi throws for non-2xx responses (e.g. the 403 an inactive
      // account gets), so the backend's extra `deactivation_reason` field
      // only survives on ApiError.data, not on a resolved result — see
      // apiErrorFromResponse() in lib/apiError.ts.
      return {
        success: false,
        message: err.message || 'An error occurred. Please try again.',
        reason: err.data?.deactivation_reason || undefined,
      };
    }
  };

  const register = async (data: any): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await bridgeApi.post<any>('ambulance_register', data);
      if (result && result.success === false) {
        return { success: false, message: result.error || result.errors?.join(', ') || 'Registration failed' };
      }
      return { success: true, message: result?.message || 'Registration submitted' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Registration failed' };
    }
  };

  const logout = () => {
    setProvider(null);
    setIsAdminSession(false);
    setProviderToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
  };

  const refreshProvider = async () => {
    if (!provider) return;

    try {
      const data = await bridgeApi.call<any>('ambulance_provider_get', { id: provider.id, token: providerToken });

      if (data) {
        // Also fetch active subscription to get the real plan name
        let activePlan = data.subscription_plan || 'free';
        try {
          const subData = await bridgeApi.call<any>('ambulance_subscription_get', { provider_id: provider.id });
          if (subData && subData.id && subData.status === 'active' && subData.plan_name) {
            activePlan = subData.plan_name;
          }
        } catch {}

        const updated: AmbulanceProvider = {
          id: data.id,
          provider_name:
            data.provider_name ||
            data.business_name ||
            data.full_name ||
            data.contact_person ||
            '',
          provider_type: data.provider_type || 'individual',
          email: data.email,
          contact_number: data.contact_number,
          alt_contact: data.alt_contact || '',
          city: data.city,
          state: data.state,
          base_location: data.base_location || '',
          pincode: data.pincode || '',
          registration_number: data.registration_number || '',
          license_url: data.license_url || '',
          photo_url: data.photo_url || '',
          image_type: data.image_type || null,
          latitude: data.latitude || 0,
          longitude: data.longitude || 0,
          kyc_status: data.kyc_status,
          rejection_reason: data.rejection_reason || '',
          is_verified: data.is_verified || false,
          is_active: data.is_active,
          subscription_plan: activePlan,
          terms_accepted: data.terms_accepted || false,
          adminSession: provider.adminSession,
          booking_enabled: data.booking_enabled === true || data.booking_enabled === '1' || data.booking_enabled === 1,
          hours_enabled: data.hours_enabled === true || data.hours_enabled === '1' || data.hours_enabled === 1,
          email_notifications: data.email_notifications === true || data.email_notifications === '1' || data.email_notifications === 1,
          sms_notifications: data.sms_notifications === true || data.sms_notifications === '1' || data.sms_notifications === 1,
          booking_alerts: data.booking_alerts === true || data.booking_alerts === '1' || data.booking_alerts === 1,
        };
        setProvider(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to refresh provider:', e);
    }
  };

  return (
    <AmbulanceAuthContext.Provider
      value={{
        provider,
        isLoading,
        isAuthenticated: !!provider,
        isAdminSession,
        providerToken,
        login,
        register,
        logout,
        refreshProvider,
      }}
    >
      {children}
    </AmbulanceAuthContext.Provider>
  );
};

export const useAmbulanceAuth = () => {
  const context = useContext(AmbulanceAuthContext);
  if (context === undefined) {
    throw new Error('useAmbulanceAuth must be used within a AmbulanceAuthProvider');
  }
  return context;
};
